// File: src/pages/CreateChallan.jsx
import React, { useEffect, useState, useContext, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Modal,
  ModalHeader,
  ModalBody,
  Card,
  CardBody,
  Col,
  Form,
  Row,
  Input,
  Label,
  Button,
  Table,
} from "reactstrap";
import Multiselect from "multiselect-react-dropdown";
import {
  _Get,
  _PostSave,
  PurchaseOrderList,
  CreateCustomerList,
  UnitListView,
} from "../import/ApiEndPoint/ApiCalling";
import {
  PurchaseProductList_Product,
  ViewOther_Charges,
  view_create_order_history,
  View_Ledger_by_id,
  // Challan endpoints
  Challan_Create,
  Challan_List,
} from "../import/ApiEndPoint/Api";
import UserContext from "../import/context/Context";
import { GstCalculation } from "../import/GstCalculation";
import swal from "sweetalert";
import "./InvoiceUI.css";

/* ------------------------------------------------------------------ */
/* ----------------------- Helpers: Rounding & GST ------------------- */
/* ------------------------------------------------------------------ */
const round2 = (n) => Number((Number(n) || 0).toFixed(2));
const floor2 = (n) => Math.floor((Number(n) || 0) * 100) / 100;
const ceil2 = (n) => Math.ceil((Number(n) || 0) * 100) / 100;

const parseGstPct = (g) => {
  if (g === null || g === undefined) return 0;
  if (typeof g === "number") return g || 0;
  const s = String(g).trim();
  if (!s) return 0;
  return s
    .split("+")
    .map((x) => Number(x) || 0)
    .reduce((a, b) => a + b, 0);
};

const saleFromMRPByGrade = (mrp, gradePct) =>
  floor2(Number(mrp || 0) / (1 + Number(gradePct || 0) / 100));
const basicFromSaleGST = (sale, gstPct) =>
  ceil2(Number(sale || 0) / (1 + Number(gstPct || 0) / 100));
const saleFromBasic = (basic, gstPct) =>
  round2(Number(basic || 0) * (1 + Number(gstPct || 0) / 100));
const basicFromSaleEditable = (sale, gstPct) => basicFromSaleGST(sale, gstPct);

/* ------------------------------------------------------------------ */
/* ---------------------- Adjustments Calculator --------------------- */
/* ------------------------------------------------------------------ */
const computeAdjustments = (base = 0, discountsRows = [], chargesRows = []) => {
  let running = Number(base) || 0;
  const discApplied = [];
  const chgApplied = [];

  // DISCOUNTS (sequential)
  (discountsRows || []).forEach((r) => {
    const row = { ...r };
    const typ = (row?.discounttype || "").toLowerCase();
    if (typ === "percentage") {
      const val = (running * (Number(row?.percentage) || 0)) / 100;
      row.__applied = ceil2(val); // ceil each step
    } else {
      row.__applied = round2(Number(row?.amount) || 0);
    }
    running -= row.__applied;
    if (running < 0) running = 0;
    discApplied.push(row);
  });

  // CHARGES (sequential on discounted running)
  (chargesRows || []).forEach((r) => {
    const row = { ...r };
    const typ = (row?.discounttype || "").toLowerCase();
    if (typ === "percentage") {
      const val = (running * (Number(row?.percentage) || 0)) / 100;
      row.__applied = round2(val);
    } else {
      row.__applied = round2(Number(row?.amount) || 0);
    }
    running += row.__applied;
    chgApplied.push(row);
  });

  const discountTotal = round2(
    discApplied.reduce((a, x) => a + (x.__applied || 0), 0)
  );
  const chargesTotal = round2(
    chgApplied.reduce((a, x) => a + (x.__applied || 0), 0)
  );

  return {
    discountTotal,
    chargesTotal,
    discountRowsApplied: discApplied,
    chargeRowsApplied: chgApplied,
    netAfterAdj: round2(running), // Basic - Discount + Charges
  };
};

/* ------------------------------------------------------------------ */
/* --------------------------- Component ----------------------------- */
/* ------------------------------------------------------------------ */

let geotagging = "";

const CreateChallan = () => {
  const roundHalfUp = (num) => {
    const frac = num - Math.floor(num);
    return frac > 0.49 ? Math.ceil(num) : Math.floor(num);
  };

  const [localRoundOff, setLocalRoundOff] = useState(0);
  const [ChallanNumber, setChallanNumber] = useState("");
  const [CustomerLimit, setCustomerLimit] = useState(0);
  const [openingLimit, setOpeningLimit] = useState(0);
  const [CustomerTerm, setCustomerTerm] = useState("");
  const [PartyLogin, setPartyLogin] = useState(false);
  const [Loading, setLoading] = useState(false);
  const [borderColor, setBorderColor] = useState("black");
  const [remarkMessage, setRemarkMessage] = useState("");
  const [remarkModel, setRemarkModel] = useState(false); // kept for UI parity, not used in submit

  const [ProductList, setProductList] = useState([]);
  const [GSTData, setGSTData] = useState({});
  const [PartyList, setPartyList] = useState([]);
  const [PartyId, setPartyId] = useState("");
  const [Party, setParty] = useState({});
  const [UnitList, setUnitList] = useState([]);
  const [chargesMaster, setChargesMaster] = useState([]);

  // Bill-level rows
  const [selectedChargesRows, setSelectedChargesRows] = useState([]);
  const [selectedDiscountRows, setSelectedDiscountRows] = useState([]);

  // Totals (Taxable/Grand)
  const [finalPrice, setFinalPrice] = useState(0); // Taxable Value
  const [taxRate, setTaxRate] = useState(0); // GST on charges
  const [finalPriceAfterTax, setFinalPriceAfterTax] = useState(0); // Grand

  const [ledgerBalance, setLedgerBalance] = useState("");
  const [pendingData, setPendingData] = useState(0);
  const [orderDate, setOrderDate] = useState("");

  const [product, setProduct] = useState([
    {
      productId: "",
      productData: "",
      disCountPercentage: 0,
      availableQty: 0,
      wholeQty: 0,
      qty: 1,
      mrp: 0,
      gradeDiscountPct: 0,
      price: 0,
      basicPrice: 0,
      salePrice: 0,
      Size: 0,
      unitType: "",
      unitPriceAfterDiscount: "",
      totalprice: "",
      taxableAmount: 0,
      gstPercentage: 0,
      sgstRate: 0,
      cgstRate: 0,
      igstRate: 0,
      grandTotal: 0,
      HSN_Code: "",
      secondaryUnit: "",
      secondarySize: 0,
      GrossQty: 0,
      primaryUnit: "",
    },
  ]);

  const multiselectRefs = useRef(product.map(() => React.createRef()));
  const Context = useContext(UserContext);
  const navigate = useNavigate();

  /* ---------------- small CSS injector (same as invoice page) ---------------- */
  useEffect(() => {
    const id = "invoice-compact-css";
    if (document.getElementById(id)) return;
    const s = document.createElement("style");
    s.id = id;
    s.textContent = `
    .compact-ui{
      --box-w: clamp(68px, 6.8vw, 110px);
      --gap: 6px;
      --fs: 13px;
      --pad: 6px;
      --label-fs: 12px;
    }
    .compact-ui label{ font-size: var(--label-fs); margin-bottom: 4px; font-weight: 600; line-height: 1.1; }
    .compact-ui .card-body{ padding: 12px; }
    .compact-ui .form-control,
    .compact-ui .form-select{
      height: calc(28px + var(--pad));
      min-height: calc(28px + var(--pad));
      padding: var(--pad) 10px;
      font-size: var(--fs);
      border-radius: 10px;
      line-height: 1.15;
    }
    .compact-ui .top-line{
      --topcol-w: clamp(160px, 14vw, 210px);
      display: grid;
      grid-auto-flow: column;
      grid-auto-columns: var(--topcol-w);
      gap: 8px;
      align-items: end;
    }
    .compact-ui .btn{ --bs-btn-padding-y: 4px; --bs-btn-padding-x: 10px; font-size: 12px; line-height: 1.1; border-radius: 10px; }
    .compact-ui .btn-sm{ padding: 3px 8px; font-size: 11.5px; border-radius: 9px; }
    .compact-ui .table, .compact-ui table{ font-size: 12px; }
    .compact-ui .table > :not(caption) > * > *{ padding: 4px 6px; }
    .compact-ui .inline-row{ display: grid; grid-auto-flow: column; grid-auto-columns: var(--box-w); gap: var(--gap); align-items: end; overflow-x: hidden; }
    .compact-ui .auto-cell{ width: var(--box-w); min-width: var(--box-w); }
    .compact-ui .auto-cell.w-wide{ grid-column: span 2; }
    .compact-ui .chip{ display: inline-flex; align-items: center; gap: 2px; padding: 2px 4px; border-radius: 9999px; font-size: 12px; background: #f3f4f6; border: 1px solid #e5e7eb; }
    .compact-ui .chip input.form-control, .compact-ui .chip select.form-select{ height: calc(26px + var(--pad)); min-width: 60px; padding: var(--pad) 8px; font-size: 12px; }
    .compact-ui .totals-ul{ width: max-content; min-width: 240px; font-size: 13px; }
    .compact-ui .totals-ul li{ padding: 4px 0; gap: 10px; }
    .compact-ui .adjustments-inline{ display: grid; grid-auto-flow: column; grid-auto-columns: max-content; gap: 8px; align-items: end; overflow-x: hidden; }
    @media (min-width: 1600px){ .compact-ui{ --box-w: 120px; } }
    @media (max-width: 1280px){ .compact-ui{ --box-w: 82px; } }
  `;
    document.head.appendChild(s);
  }, []);

  useEffect(() => {
    const id = "multiselect-chip-fix";
    if (document.getElementById(id)) return;
    const s = document.createElement("style");
    s.id = id;
    s.textContent = `
      .multiselect-container .searchWrapper{ overflow: visible !important; }
      .multiselect-container .chip{ display: inline-flex !important; align-items: center; gap: 6px; }
      .multiselect-container .chip > span{ white-space: nowrap !important; }
    `;
    document.head.appendChild(s);
  }, []);

  /* ---------------- load charges/discount master ---------------- */
  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem("userData"));
    if (userData?.database) {
      _Get(ViewOther_Charges, userData.database)
        .then((res) => setChargesMaster(res?.OtherCharges || []))
        .catch(() => setChargesMaster([]));
    }
  }, []);

  /* ---------------- helpers ---------------- */
  const sumLineBasics = (rows) =>
    (rows || []).reduce(
      (s, r) =>
        s + Number(r?.qty || 0) * Number(r?.basicPrice || r?.price || 0),
      0
    );

  /* =================================================================
     TOTALS (FIXED): write back computed rows
     ================================================================= */
  const recalcTotals = (
    _chargesRows = selectedChargesRows,
    _discountRows = selectedDiscountRows
  ) => {
    const basicTotal = sumLineBasics(product);

    const {
      chargesTotal,
      discountTotal,
      discountRowsApplied,
      chargeRowsApplied,
      netAfterAdj, // Basic - Discount + Charges
    } = computeAdjustments(basicTotal, _discountRows, _chargesRows);

    setSelectedDiscountRows(discountRowsApplied);
    setSelectedChargesRows(chargeRowsApplied);

    // GST on charges at highest item GST%
    const maxItemGST =
      product.reduce(
        (m, p) => Math.max(m, Number(parseGstPct(p?.gstPercentage || 0))),
        0
      ) || 0;

    const isIGST = !!GSTData?.Tax?.IgstTaxType;
    const chargesGST = (chargesTotal * maxItemGST) / 100;
    const half = chargesGST / 2;

    let addIGST = 0,
      addCGST = 0,
      addSGST = 0;
    if (isIGST) addIGST = chargesGST;
    else {
      addCGST = half;
      addSGST = half;
    }

    // Taxable Value
    setFinalPrice(Number(netAfterAdj).toFixed(2));
    // GST on charges only
    setTaxRate(Number(addIGST + addCGST + addSGST));

    // Items GST from GstCalculation (items-only)
    const itemsGST = isIGST
      ? Number(GSTData?.Tax?.IgstTotal || 0)
      : Number(GSTData?.Tax?.CgstTotal || 0) +
        Number(GSTData?.Tax?.SgstTotal || 0);

    // Gross = Taxable + item GST + charge GST
    const gross =
      Number(netAfterAdj || 0) +
      Number(itemsGST || 0) +
      Number(addIGST + addCGST + addSGST);

    const rounded = roundHalfUp(gross);
    const roundOff = Number((rounded - gross).toFixed(2));

    setLocalRoundOff(roundOff);
    setFinalPriceAfterTax(rounded);
  };

  /* ---------------- HSN Summary with CHARGES row ---------------- */
  const hsnSummary = useMemo(() => {
    const details = Array.isArray(GSTData?.gstDetails)
      ? GSTData.gstDetails
      : [];
    const isIGST = !!GSTData?.Tax?.IgstTaxType;

    const byHSN = new Map();
    details.forEach((r) => {
      const hsn = String(r?.hsn || "NA");
      const ratePct = parseGstPct(r?.gstPercentage || 0);
      const g = byHSN.get(hsn) || {
        hsn,
        ratePct,
        taxable: 0,
        cgst: 0,
        sgst: 0,
        igst: 0,
      };
      g.ratePct = ratePct;
      g.taxable += Number(r?.taxable || 0);
      g.cgst += Number(r?.cgstRate || 0);
      g.sgst += Number(r?.sgstRate || 0);
      g.igst += Number(r?.igstRate || 0);
      byHSN.set(hsn, g);
    });

    let maxRate = 0;
    for (const v of byHSN.values()) maxRate = Math.max(maxRate, v.ratePct);

    const chargesAmt = (selectedChargesRows || []).reduce(
      (s, x) => s + (Number(x?.__applied) || 0),
      0
    );
    const chargesTax = (chargesAmt * maxRate) / 100;

    const rows = Array.from(byHSN.values())
      .map((r) => ({
        kind: "ITEM",
        hsn: r.hsn,
        ratePct: r.ratePct,
        taxable: round2(r.taxable),
        cgst: round2(r.cgst),
        sgst: round2(r.sgst),
        igst: round2(r.igst),
        total: round2(r.taxable + r.cgst + r.sgst + r.igst),
      }))
      .sort((a, b) => b.ratePct - a.ratePct);

    if (chargesAmt > 0) {
      rows.push({
        kind: "CHARGE",
        hsn: `CHARGES @ ${maxRate}%`,
        ratePct: maxRate,
        taxable: round2(chargesAmt),
        cgst: isIGST ? 0 : round2(chargesTax / 2),
        sgst: isIGST ? 0 : round2(chargesTax / 2),
        igst: isIGST ? round2(chargesTax) : 0,
        total: round2(chargesAmt + chargesTax),
      });
    }

    const totals = rows.reduce(
      (acc, r) => ({
        taxable: acc.taxable + r.taxable,
        cgst: acc.cgst + (r.cgst || 0),
        sgst: acc.sgst + (r.sgst || 0),
        igst: acc.igst + (r.igst || 0),
        total: acc.total + r.total,
      }),
      { taxable: 0, cgst: 0, sgst: 0, igst: 0, total: 0 }
    );

    return { rows, totals, isIGST };
  }, [GSTData, selectedChargesRows]);

  /* ---------------- Party selection ---------------- */
  const uniqueChallanSeries = async () => {
    try {
      const userData = JSON.parse(localStorage.getItem("userData"));
      const res = await _Get(Challan_List, userData._id, userData.database);
      const challans = Array.isArray(res?.challans) ? res.challans : [];

      const d = new Date();
      const YY = String(d.getFullYear()).slice(-2);
      const MM = String(d.getMonth() + 1).padStart(2, "0");
      const DD = String(d.getDate()).padStart(2, "0");
      const prefix = `CH-${YY}${MM}${DD}-`;

      const sameDay = challans
        .map((c) => String(c?.challanId || ""))
        .filter((id) => id.startsWith(prefix));

      let maxSeq = 0;
      sameDay.forEach((id) => {
        const tail = id.split("-").pop();
        const n = parseInt(tail, 10);
        if (!Number.isNaN(n) && n > maxSeq) maxSeq = n;
      });

      const next = String(maxSeq + 1).padStart(4, "0");
      return `${prefix}${next}`;
    } catch {
      return ""; // backend will auto-generate if empty
    }
  };

  const handleSelectionParty = async (_selectedList, selectedItem) => {
    setOpeningLimit(selectedItem?.limit);

    let openingBalance = Number(selectedItem?.OpeningBalance) || 0;
    let openingType = (selectedItem?.Type || "").toLowerCase();
    let totalDebit = 0;
    let totalCredit = 0;

    try {
      const res = await _Get(View_Ledger_by_id, selectedItem?._id);
      if (res?.Ledger?.length > 0) {
        res.Ledger.forEach((entry) => {
          totalDebit += Number(entry?.debit) || 0;
          totalCredit += Number(entry?.credit) || 0;
        });
      }
    } catch {}

    if (openingType === "debit") totalDebit += openingBalance;
    else if (openingType === "credit") totalCredit += openingBalance;

    const finalBalance = totalDebit - totalCredit;
    const balanceType = finalBalance >= 0 ? "Debit" : "Credit";
    const led = `${Math.abs(finalBalance)} (${balanceType})`;
    setLedgerBalance(led);

    // Prefill challan series (you can overwrite)
    const series = await uniqueChallanSeries();
    if (series) setChallanNumber(series);

    setPartyId(selectedItem._id);
    setParty(selectedItem);

    const partyGrade = Number(selectedItem?.category?.discount || 0);
    setProduct((prev) =>
      prev.map((row) => {
        if (!row?.productData) return row;
        const mrp = Number(row?.productData?.Product_MRP || 0);
        const gstPct = Number(row?.productData?.GSTRate || 0);
        const sale = saleFromMRPByGrade(mrp, partyGrade);
        const basic = basicFromSaleGST(sale, gstPct);
        return {
          ...row,
          mrp,
          gradeDiscountPct: partyGrade,
          gstPercentage: gstPct,
          salePrice: sale,
          basicPrice: basic,
          price: basic,
        };
      })
    );

    const gstdetails = GstCalculation(
      selectedItem,
      product,
      Context,
      selectedDiscountRows
    );
    setGSTData(gstdetails);

    const isCash = (selectedItem?.paymentTerm || "")
      .toLowerCase()
      .includes("cash");

    if (isCash) {
      setCustomerLimit(0);
      setCustomerTerm("Cash");
      setBorderColor("green");
    } else {
      try {
        const userData = JSON.parse(localStorage.getItem("userData"));
        const salesRes = await _Get(
          view_create_order_history,
          userData?._id,
          userData?.database
        );
        const pendingSales = salesRes?.orderHistory?.filter(
          (order) =>
            order.status === "pending" &&
            order?.partyId?._id === selectedItem?._id
        );
        const totalPending =
          pendingSales?.reduce((acc, cur) => acc + (cur.grandTotal || 0), 0) ||
          0;

        const purchaseRes = await PurchaseOrderList(
          userData?._id,
          userData?.database
        );
        const pendingPurchase = purchaseRes?.orderHistory?.filter(
          (po) =>
            po.status === "pending" && po?.partyId?._id === selectedItem?._id
        );
        const purchasePending =
          pendingPurchase?.reduce(
            (acc, cur) => acc + (cur.grandTotal || 0),
            0
          ) || 0;

        const adjustedPending = purchasePending - totalPending;
        setPendingData(adjustedPending);

        totalDebit = 0;
        totalCredit = 0;

        const ledgerRes = await _Get(View_Ledger_by_id, selectedItem?._id);
        if (ledgerRes?.Ledger?.length > 0) {
          ledgerRes.Ledger.forEach((entry) => {
            totalDebit += Number(entry?.debit) || 0;
            totalCredit += Number(entry?.credit) || 0;
          });
        }

        if (openingType === "debit") totalDebit += openingBalance;
        else if (openingType === "credit") totalCredit += openingBalance;

        const finalBalance2 = totalDebit - totalCredit;
        const balanceType2 = finalBalance2 >= 0 ? "Debit" : "Credit";
        const led2 = `${Math.abs(finalBalance2)} (${balanceType2})`;
        setLedgerBalance(led2);

        let finalLimit =
          balanceType2 === "Debit"
            ? selectedItem?.limit - Math.abs(finalBalance2)
            : selectedItem?.limit + Math.abs(finalBalance2);

        finalLimit += adjustedPending;
        setCustomerLimit(finalLimit);

        const grandTotal = gstdetails?.Tax?.GrandTotal || 0;
        setBorderColor(grandTotal < finalLimit ? "green" : "red");
        setCustomerTerm("");
      } catch {
        setCustomerLimit(selectedItem?.limit || 0);
        setBorderColor("green");
      }
    }

    const newDetails = GstCalculation(
      selectedItem,
      product,
      Context,
      selectedDiscountRows
    );
    setGSTData(newDetails);
    recalcTotals(selectedChargesRows, selectedDiscountRows);
  };

  const onRemoveParty = () => {
    setPartyId("");
    setParty({});
    setGSTData({});
    setCustomerLimit(0);
    setBorderColor("black");
    multiselectRefs.current.forEach((ref) =>
      ref?.current?.resetSelectedValues?.()
    );
    setProduct([
      {
        productId: "",
        productData: "",
        disCountPercentage: 0,
        availableQty: 0,
        wholeQty: 0,
        qty: 1,
        mrp: 0,
        gradeDiscountPct: 0,
        price: 0,
        basicPrice: 0,
        salePrice: 0,
        Size: 0,
        unitType: "",
        unitPriceAfterDiscount: "",
        totalprice: "",
        taxableAmount: 0,
        gstPercentage: 0,
        sgstRate: 0,
        cgstRate: 0,
        igstRate: 0,
        grandTotal: 0,
        HSN_Code: "",
        secondaryUnit: "",
        GrossQty: 0,
        primaryUnit: "",
      },
    ]);
    setSelectedChargesRows([]);
    setSelectedDiscountRows([]);
    setFinalPrice(0);
    setTaxRate(0);
    setFinalPriceAfterTax(0);
    setChallanNumber("");
  };

  /* ---------------- Product selection ---------------- */
  const handleSelection = async (_selectedList, selectedItem, index) => {
    if (!(selectedItem?.qty > 0)) {
      swal("Error", "Stock Not Available", "error");
      return;
    }

    const partyGrade = Number(Party?.category?.discount || 0);
    const mrp = Number(selectedItem?.Product_MRP || 0);
    const gstPct = Number(selectedItem?.GSTRate || 0);

    const sale = saleFromMRPByGrade(mrp, partyGrade);
    const basic = basicFromSaleGST(sale, gstPct);

    setProduct((prev) => {
      const updated = [...prev];
      const row = { ...(updated[index] || {}) };

      row.productId = selectedItem?._id;
      row.productData = selectedItem;
      row.mrp = mrp;
      row.gradeDiscountPct = partyGrade;
      row.gstPercentage = gstPct;
      row.salePrice = sale;
      row.basicPrice = basic;
      row.price = basic;

      row.HSN_Code = selectedItem?.HSN_Code;
      row.primaryUnit = selectedItem?.primaryUnit;
      row.secondaryUnit = selectedItem?.secondaryUnit;
      row.secondarySize = selectedItem?.secondarySize;
      row.GrossQty = Number(
        (1 / (selectedItem?.secondarySize || 1)).toFixed(3)
      );

      row.disCountPercentage = partyGrade;
      row.availableQty = selectedItem?.qty;
      row.wholeQty = selectedItem?.qty;

      updated[index] = row;

      const gstdetails = GstCalculation(
        Party,
        updated,
        Context,
        selectedDiscountRows
      );
      setGSTData(gstdetails);

      const det = gstdetails?.gstDetails?.[index] || {};
      row.taxableAmount = round2(
        (row.qty || 0) * (row.basicPrice || row.price || 0)
      );
      row.sgstRate = det?.sgstRate || 0;
      row.cgstRate = det?.cgstRate || 0;
      row.igstRate = det?.igstTax?.[0]?.amount || 0;
      row.grandTotal = det?.grandTotal || 0;

      updated[index] = row;
      setProduct(updated);
      recalcTotals();

      return updated;
    });
  };

  const onRemove1 = (_selectedList, _removedItem, index) => {
    const updated = [...product];
    updated[index] = {
      productId: "",
      productData: "",
      disCountPercentage: 0,
      availableQty: 0,
      wholeQty: 0,
      qty: 1,
      mrp: 0,
      gradeDiscountPct: 0,
      price: 0,
      basicPrice: 0,
      salePrice: 0,
      Size: 0,
      unitType: "",
      unitPriceAfterDiscount: "",
      totalprice: "",
      taxableAmount: 0,
      gstPercentage: 0,
      sgstRate: 0,
      cgstRate: 0,
      igstRate: 0,
      grandTotal: 0,
      HSN_Code: "",
      secondaryUnit: "",
      GrossQty: 0,
      primaryUnit: "",
    };
    setProduct(updated);

    const gstdetails = GstCalculation(
      Party,
      updated,
      Context,
      selectedDiscountRows
    );
    setGSTData(gstdetails);
    recalcTotals();
  };

  /* ---------------- Qty & price handlers ---------------- */
  const handleRequredQty = (e, index, availableQty, CustomerTermState) => {
    const list = [...product];
    const { name, value } = e.target;

    const asNum = value === "" ? "" : Number(value);
    if (value !== "" && isNaN(asNum)) return;

    const nextNetQty =
      name === "GrossQty"
        ? Number(((list[index]?.secondarySize || 1) * (asNum || 0)).toFixed(3))
        : asNum || 0;

    if (value !== "" && (nextNetQty < 0 || nextNetQty > (availableQty || 0))) {
      return;
    }

    if (name === "GrossQty") {
      list[index].GrossQty = value === "" ? 0 : asNum;
      list[index].qty = nextNetQty;
    } else {
      list[index][name] = value === "" ? 0 : asNum;
      list[index].GrossQty = Number(
        ((list[index].qty || 0) / (list[index]?.secondarySize || 1)).toFixed(3)
      );
    }

    const gstdetails = GstCalculation(
      Party,
      list,
      Context,
      selectedDiscountRows
    );
    setGSTData(gstdetails);

    const gstInfo = gstdetails?.gstDetails?.[index] || {};
    list[index].taxableAmount = round2(
      (list[index]?.qty || 0) *
        (list[index]?.basicPrice || list[index]?.price || 0)
    );
    list[index].sgstRate = gstInfo?.sgstRate || 0;
    list[index].cgstRate = gstInfo?.cgstRate || 0;
    list[index].igstRate = gstInfo?.igstTax?.[0]?.amount || 0;
    list[index].grandTotal = gstInfo?.grandTotal || 0;
    list[index].gstPercentage = gstInfo?.gstPercentage || 0;
    list[index].disCountPercentage = gstInfo?.discountPercentage || 0;

    setProduct(list);

    if (CustomerTermState !== "Cash") {
      if ((gstdetails?.Tax?.GrandTotal || 0) < (CustomerLimit || 0)) {
        setBorderColor("green");
      } else {
        setBorderColor("red");
      }
    }

    recalcTotals();
  };

  const handleChangePrice = (e, index) => {
    const { name, value } = e.target;
    const v = value === "" ? "" : Number(value);
    if (value !== "" && (isNaN(v) || v <= 0)) return;

    const list = [...product];
    if (!list[index]) return;

    const row = { ...list[index] };
    const gstPct = parseGstPct(row?.gstPercentage);

    if (name === "basicPrice") {
      row.basicPrice = v === "" ? 0 : v;
      row.price = v === "" ? 0 : v;
      row.salePrice = saleFromBasic(v || 0, gstPct);
    } else if (name === "salePrice") {
      row.salePrice = v === "" ? 0 : v;
      const basic = basicFromSaleEditable(v || 0, gstPct);
      row.basicPrice = basic;
      row.price = basic;
    } else {
      row[name] = v === "" ? 0 : v;
    }

    list[index] = row;

    const gstdetails = GstCalculation(
      Party,
      list,
      Context,
      selectedDiscountRows
    );
    setGSTData(gstdetails);

    if (gstdetails?.gstDetails && gstdetails?.gstDetails[index]) {
      row.taxableAmount = round2(
        (row.qty || 0) * (row.basicPrice || row.price || 0)
      );
      row.sgstRate = gstdetails.gstDetails[index]?.sgstRate || 0;
      row.cgstRate = gstdetails.gstDetails[index]?.cgstRate || 0;
      row.igstRate = gstdetails.gstDetails[index]?.igstTax?.[0]?.amount || 0;
      row.grandTotal = gstdetails.gstDetails[index]?.grandTotal || 0;
    }

    list[index] = row;
    setProduct(list);

    if (CustomerTerm !== "Cash") {
      if ((gstdetails?.Tax?.GrandTotal || 0) < (CustomerLimit || 0)) {
        setBorderColor("green");
      } else {
        setBorderColor("red");
      }
    }
    recalcTotals();
  };

  /* ---------------- Add/Remove product rows ---------------- */
  const addMoreProduct = () => {
    setProduct([
      ...product,
      {
        productId: "",
        productData: "",
        disCountPercentage: 0,
        availableQty: 0,
        wholeQty: 0,
        qty: 1,
        mrp: 0,
        gradeDiscountPct: 0,
        price: 0,
        basicPrice: 0,
        salePrice: 0,
        Size: 0,
        unitType: "",
        unitPriceAfterDiscount: "",
        totalprice: "",
        taxableAmount: 0,
        gstPercentage: 0,
        sgstRate: 0,
        cgstRate: 0,
        igstRate: 0,
        grandTotal: 0,
        HSN_Code: "",
        secondaryUnit: "",
        GrossQty: 0,
        primaryUnit: "",
      },
    ]);
  };

  const removeMoreProduct = (i) => {
    const newFormValues = [...product];
    newFormValues.splice(i, 1);
    setProduct(newFormValues);
    const gstdetails = GstCalculation(
      Party,
      newFormValues,
      Context,
      selectedDiscountRows
    );
    setGSTData(gstdetails);
    recalcTotals();
  };

  /* ---------------- Charges & Discount rows handlers ---------------- */
  const addChargeRow = () =>
    setSelectedChargesRows((prev) => [
      ...prev,
      {
        id: "",
        title: "",
        type: "Charges",
        discounttype: "",
        percentage: "",
        amount: "",
        __applied: 0,
      },
    ]);

  const addDiscountRow = () =>
    setSelectedDiscountRows((prev) => [
      ...prev,
      {
        id: "",
        title: "",
        type: "Discount",
        discounttype: "",
        percentage: "",
        amount: "",
        __applied: 0,
      },
    ]);

  const removeChargeRow = (i) => {
    const arr = [...selectedChargesRows];
    arr.splice(i, 1);
    setSelectedChargesRows(arr);
    recalcTotals(arr, selectedDiscountRows);
  };

  const removeDiscountRow = (i) => {
    const arr = [...selectedDiscountRows];
    arr.splice(i, 1);
    setSelectedDiscountRows(arr);
    const gstdetails = GstCalculation(Party, product, Context, arr);
    setGSTData(gstdetails);
    recalcTotals(selectedChargesRows, arr);
  };

  const pickMasterRow = (rowType, idx, id) => {
    const master = chargesMaster.find((x) => x._id === id);
    if (!master) return;

    const rowData = {
      id: master._id,
      title: master.title,
      type: master.type,
      discounttype: master.discounttype,
      percentage: master.percentage || "",
      amount: master.amount || "",
      __applied: 0,
    };

    if (rowType === "Charges") {
      const arr = [...selectedChargesRows];
      arr[idx] = rowData;
      setSelectedChargesRows(arr);
      recalcTotals(arr, selectedDiscountRows);
    } else {
      const arr = [...selectedDiscountRows];
      arr[idx] = rowData;
      const gstdetails = GstCalculation(Party, product, Context, arr);
      setGSTData(gstdetails);
      setSelectedDiscountRows(arr);
      recalcTotals(selectedChargesRows, arr);
    }
  };

  const changeRowField = (rowType, idx, key, value) => {
    const norm = (k, v) => {
      if (k === "percentage" || k === "amount") {
        if (v === "" || v === null) return "";
        const n = Number(v);
        return isNaN(n) ? "" : n;
      }
      return v;
    };

    if (rowType === "Charges") {
      const arr = [...selectedChargesRows];
      arr[idx] = { ...arr[idx], [key]: norm(key, value) };
      setSelectedChargesRows(arr);
      recalcTotals(arr, selectedDiscountRows);
    } else {
      const arr = [...selectedDiscountRows];
      arr[idx] = { ...arr[idx], [key]: norm(key, value) };
      setSelectedDiscountRows(arr);
      const gstdetails = GstCalculation(Party, product, Context, arr);
      setGSTData(gstdetails);
      recalcTotals(selectedChargesRows, arr);
    }
  };

  const onAddAdjustmentSelect = (e) => {
    const id = e.target.value;
    if (!id) return;
    e.target.value = "";
    const master = chargesMaster.find((x) => x._id === id);
    if (!master) return;

    const rowData = {
      id: master._id,
      title: master.title,
      type: master.type,
      discounttype: master.discounttype,
      percentage: master.percentage || "",
      amount: master.amount || "",
      __applied: 0,
    };

    if ((master.type || "").toLowerCase().includes("discount")) {
      const arr = [...selectedDiscountRows, rowData];
      setSelectedDiscountRows(arr);
      const gstdetails = GstCalculation(Party, product, Context, arr);
      setGSTData(gstdetails);
      recalcTotals(selectedChargesRows, arr);
    } else {
      const arr = [...selectedChargesRows, rowData];
      setSelectedChargesRows(arr);
      recalcTotals(arr, selectedDiscountRows);
    }
  };

  /* ---------------- Submit ---------------- */
  const handleProceed = async () => {
    try {
      setLoading(true);

      const basicTotal = sumLineBasics(product);

      const {
        chargesTotal,
        discountTotal,
        discountRowsApplied,
        chargeRowsApplied,
        netAfterAdj,
      } = computeAdjustments(
        basicTotal,
        selectedDiscountRows,
        selectedChargesRows
      );

      const igstTaxType = !!GSTData?.Tax?.IgstTaxType;

      // Highest GST% among items (for charges taxation)
      const highestGstPct = (() => {
        let maxPct = 0;
        (product || []).forEach((p) => {
          const g = parseGstPct(p?.gstPercentage);
          if (!isNaN(g) && g > maxPct) maxPct = g;
        });
        (GSTData?.gstDetails || []).forEach((row) => {
          const g = parseGstPct(row?.gstPercentage);
          if (!isNaN(g) && g > maxPct) maxPct = g;
        });
        return maxPct || 0;
      })();

      let chargesCgst = 0,
        chargesSgst = 0,
        chargesIgst = 0;
      if (chargesTotal > 0 && highestGstPct > 0) {
        const taxOnCharges = (chargesTotal * highestGstPct) / 100;
        if (igstTaxType) chargesIgst = taxOnCharges;
        else {
          chargesCgst = taxOnCharges / 2;
          chargesSgst = taxOnCharges / 2;
        }
      }

      const baseSgst = Number(GSTData?.Tax?.SgstTotal || 0);
      const baseCgst = Number(GSTData?.Tax?.CgstTotal || 0);
      const baseIgst = Number(GSTData?.Tax?.IgstTotal || 0);

      const finalSgst = igstTaxType ? 0 : baseSgst + chargesSgst;
      const finalCgst = igstTaxType ? 0 : baseCgst + chargesCgst;
      const finalIgst = igstTaxType ? baseIgst + chargesIgst : 0;

      const itemsGrand = Array.isArray(GSTData?.gstDetails)
        ? GSTData.gstDetails.reduce(
            (acc, row) => acc + (Number(row?.grandTotal) || 0),
            0
          )
        : 0;

      const grandTotal =
        itemsGrand +
        chargesTotal +
        chargesCgst +
        chargesSgst +
        chargesIgst +
        localRoundOff;

      const orderItemsPayload = product.map((ele) => {
        const qty = Number(ele?.qty) || 0;
        const base = Number(ele?.basicPrice || ele?.price || 0);
        return {
          productId: ele?.productId,
          discountPercentage: Number(ele?.disCountPercentage) || 0,
          qty,
          price: base,
          Size: Number(ele?.secondarySize || 0),
          unitType: ele?.primaryUnit || "",
          totalPrice: Number(qty * base),
          gstPercentage: parseGstPct(ele?.gstPercentage),
          sgstTotal: Number(ele?.sgstRate) || 0,
          cgstTotal: Number(ele?.cgstRate) || 0,
          igstTotal: Number(ele?.igstRate) || 0,
          grandTotal: String(Number(ele?.grandTotal) || 0),
          taxableAmount: Number(qty * (Number(ele?.taxableAmount) || base)),
          totalPriceWithDiscount: Number(ele?.grandTotal) || 0,
          warehouse: "", // set if you capture warehouse here
          status: "ordered",
        };
      });

      const fullname = Party?.CompanyName || Party?.fullName || "";
      const userData = JSON.parse(localStorage.getItem("userData")) || {};

      // Build payload for Challan backend
      const payload = {
        userId: userData?._id,
        partyId: Party?._id || PartyId,
        challanId: ChallanNumber || undefined, // backend will generate if undefined/empty
        database: userData?.database,

        date: orderDate,
        fullName: fullname,
        address: Party?.address,
        MobileNo: Party?.contactNumber,
        pincode: Party?.pincode,
        state: Party?.State,
        city: Party?.City,

        igstTaxType: igstTaxType ? 1 : 0,

        // Header-level economics (backend recomputes; send for clarity)
        amount: Number(netAfterAdj.toFixed(2)), // taxable
        grandTotal: Number(grandTotal.toFixed(2)),
        roundOff: Number(localRoundOff.toFixed(2)),
        sgstTotal: Number(finalSgst.toFixed(2)),
        cgstTotal: Number(finalCgst.toFixed(2)),
        igstTotal: Number(finalIgst.toFixed(2)),

        // Discounts/Charges summary for backend computeTotals()
        discountAmount: Number(
          (selectedDiscountRows || []).reduce(
            (a, r) => a + (r.__applied || 0),
            0
          )
        ),
        otherCharges: Number(
          (selectedChargesRows || []).reduce(
            (a, r) => a + (r.__applied || 0),
            0
          )
        ),
        shippingCost: 0,

        orderItems: orderItemsPayload,
        gstDetails: GSTData?.gstDetails || [],

        currentAddress: geotagging,
      };

      if (!payload.partyId) {
        swal("Select a customer first.");
        setLoading(false);
        return;
      }

      await _PostSave(Challan_Create, payload);

      swal("Challan Created Successfully");
      navigate(-1); // go back; change to your challan list route if you prefer
    } catch (err) {
      const msg =
        err?.response?.data?.message || err?.message || "SomeThing Went Wrong";
      swal(msg);
    } finally {
      setLoading(false);
    }
  };

  const submitHandler = async (e) => {
    e.preventDefault();
    handleProceed();
  };

  /* ---------------- init loads ---------------- */
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          geotagging = `${position.coords.latitude},${position.coords.longitude}`;
        },
        () => {},
        { timeout: 10000, enableHighAccuracy: true }
      );
    }

    const userdata = JSON.parse(localStorage.getItem("userData"));
    const findParty = userdata?.rolename?.roleName === "Customer";
    if (findParty) {
      setPartyLogin(true);
      setPartyId(userdata?._id);
      setParty(userdata);
      // optional: load limit if needed for color bar
    }

    _Get(PurchaseProductList_Product, userdata?.database)
      .then((res) => setProductList(res?.Product))
      .catch(() => {});

    let id = "";
    if (userdata.rolename.roleName === "SuperAdmin") id = userdata._id;
    else id = userdata.created_by;

    CreateCustomerList(id, userdata?.database)
      .then((res) => {
        const value = res?.Customer;
        if (value?.length) {
          const formattedData = value?.map((item) => ({
            ...item,
            displayLabel: `${item.CompanyName} (Grade: ${
              item?.category?.grade
                ? item?.category?.grade?.toUpperCase()
                : "NA"
            })`,
          }));
          setPartyList(formattedData);
        }
      })
      .catch(() => {});

    UnitListView(userdata?._id, userdata?.database)
      .then((res) => setUnitList(res.Unit))
      .catch(() => {});
  }, []);

  useEffect(() => {
    const today = new Date();
    const formattedDate = today.toISOString().slice(0, 10);
    setOrderDate(formattedDate);
  }, []);

  const styles = {
    headerRow: { margin: "0.5rem" },
    title: { margin: 0, fontSize: 22, fontWeight: 600 },
    right: { display: "flex", justifyContent: "flex-end" },
  };

  /* ------------------------------ UI ------------------------------ */
  return (
    <div className="invoice-ui">
      <Card>
        <Row style={styles.headerRow}>
          <Col>
            <h1 style={styles.title}>Create Challan</h1>
          </Col>
          <Col style={styles.right}>
            <Button color="danger" onClick={() => navigate(-1)}>
              Back
            </Button>
          </Col>
        </Row>

        <CardBody>
          <Form onSubmit={submitHandler}>
            {/* ================= TOP STRIP ================= */}
            {!PartyLogin && (
              <div className="top-strip">
                {/* Choose Party */}
                <div className="box party-box">
                  <div className="d-flex justify-content-between align-items-center mb-1">
                    <Label>
                      Choose Party <span style={{ color: "red" }}>*</span>
                    </Label>
                    <div className="d-flex gap-1">
                      <Button
                        size="sm"
                        color="secondary"
                        type="button"
                        className="btn-xxs"
                        onClick={() => {
                          if (PartyId)
                            navigate(`/app/customers/create/${PartyId}`);
                          else swal("Select a customer first");
                        }}
                      >
                        Edit
                      </Button>
                      <Button
                        size="sm"
                        color="primary"
                        type="button"
                        className="btn-xxs"
                        onClick={() => navigate(`/app/customers/create/0`)}
                      >
                        New
                      </Button>
                    </div>
                  </div>

                  <Multiselect
                    required
                    selectionLimit={1}
                    isObject
                    options={PartyList}
                    onSelect={handleSelectionParty}
                    onRemove={onRemoveParty}
                    displayValue="displayLabel"
                  />

                  {/* Optional note space kept for UI parity */}
                  {borderColor && (
                    <small style={{ display: "inline-block", marginTop: 5 }}>
                      <span style={{ fontWeight: 600, color: borderColor }}>
                        Credit Guard:
                      </span>{" "}
                      {(CustomerLimit || 0) > 0 ? "Enabled" : "Cash/NA"}
                    </small>
                  )}
                </div>

                <div className="box orderdate-box">
                  <Label>
                    Challan Date <span style={{ color: "red" }}>*</span>
                  </Label>
                  <Input
                    required
                    type="date"
                    name="orderDate"
                    value={orderDate}
                    onChange={(e) => setOrderDate(e.target.value)}
                  />
                </div>

                <div className="box invoiceno-box">
                  <Label>Challan Number</Label>
                  <div className="d-flex gap-1">
                    <Input
                      type="text"
                      placeholder="Leave blank for Auto"
                      name="ChallanNumber"
                      value={ChallanNumber}
                      onChange={(e) => setChallanNumber(e.target.value)}
                    />
                    <Button
                      type="button"
                      color="secondary"
                      onClick={async () => {
                        const auto = await uniqueChallanSeries();
                        if (auto) setChallanNumber(auto);
                        else
                          swal(
                            "Series will be generated automatically on save."
                          );
                      }}
                    >
                      Auto
                    </Button>
                  </div>
                </div>

                {/* Credit customer info (same visuals) */}
                {CustomerLimit > 0 ? (
                  <>
                    <div className="box sanction-box">
                      <Label>Sanction Amount</Label>
                      <Input
                        readOnly
                        type="text"
                        name="CustomerLimit"
                        value={Number(openingLimit || 0).toFixed(2)}
                      />
                      <div className="note-red">Ledger B: {ledgerBalance}</div>
                    </div>

                    <div className="box pending-box">
                      <Label>Pending Amount</Label>
                      <Input
                        readOnly
                        type="text"
                        value={Number(pendingData || 0).toFixed(2)}
                      />
                      <div className="note-red">Pending Sales</div>
                    </div>

                    <div className="box balance-box">
                      <Label>Balance Amount</Label>
                      <Input
                        readOnly
                        type="text"
                        value={Number(CustomerLimit || 0).toFixed(2)}
                      />
                    </div>

                    <div className="box netbalance-box">
                      <Label style={{ color: borderColor }}>Net Balance</Label>
                      <Input
                        readOnly
                        type="text"
                        style={{ border: `3px solid ${borderColor}` }}
                        value={
                          !!GSTData?.Tax?.GrandTotal
                            ? (
                                CustomerLimit - (GSTData?.Tax?.GrandTotal || 0)
                              ).toFixed(2)
                            : CustomerLimit
                        }
                      />
                    </div>
                  </>
                ) : (
                  <div className="box balance-box">
                    <Label>Payment Mode *</Label>
                    <Input readOnly type="text" value={CustomerTerm} />
                  </div>
                )}
              </div>
            )}

            {/* ================= PRODUCT ROWS ================= */}
            {product?.length > 0 &&
              product?.map((p, index) => (
                <div key={index} className="prod-row mt-2">
                  <div className="box prod-select-box">
                    <div className="d-flex justify-content-between align-items-center mb-1">
                      <Label>
                        Select Product <span style={{ color: "red" }}>*</span>
                      </Label>
                      <div className="d-flex gap-1">
                        <Button
                          size="sm"
                          color="secondary"
                          type="button"
                          className="btn-xxs"
                          onClick={() => {
                            const pid = product?.[index]?.productId;
                            if (pid) navigate(`/app/products/edit/${pid}`);
                            else swal("Select a product first");
                          }}
                        >
                          Edit
                        </Button>
                        <Button
                          size="sm"
                          color="primary"
                          type="button"
                          className="btn-xxs"
                          onClick={() => navigate(`/app/products/create`)}
                        >
                          New
                        </Button>
                      </div>
                    </div>

                    <Multiselect
                      required
                      ref={multiselectRefs.current[index]}
                      selectionLimit={1}
                      isObject
                      options={ProductList}
                      onSelect={(sl, si) => handleSelection(sl, si, index)}
                      onRemove={(sl, si) => onRemove1(sl, si, index)}
                      displayValue="Product_Title"
                    />
                  </div>

                  <div className="box hsn-box">
                    <Label>HSN Code</Label>
                    <Input readOnly value={p?.HSN_Code || ""} />
                  </div>

                  <div className="box grossunit-box">
                    <Label>Gross Unit</Label>
                    <Input readOnly value={p?.secondaryUnit || ""} />
                  </div>

                  <div className="box grossqty-box">
                    <Label>Gross Qty</Label>
                    <Input
                      required
                      type="number"
                      step="any"
                      name="GrossQty"
                      value={p?.GrossQty || 0}
                      onChange={(e) =>
                        handleRequredQty(
                          e,
                          index,
                          p?.availableQty,
                          CustomerTerm
                        )
                      }
                    />
                  </div>

                  <div className="box availqty-box">
                    <Label>Avail Qty *</Label>
                    <Input readOnly value={p?.availableQty || 0} />
                  </div>

                  <div className="box netqty-box">
                    <Label>Net Qty *</Label>
                    <Input
                      required
                      type="number"
                      name="qty"
                      min={1}
                      value={p?.qty || 1}
                      onChange={(e) =>
                        handleRequredQty(
                          e,
                          index,
                          p?.availableQty,
                          CustomerTerm
                        )
                      }
                    />
                  </div>

                  <div className="box netunit-box">
                    <Label>Net Unit</Label>
                    <Input disabled value={p?.primaryUnit || ""} />
                  </div>

                  <div className="box mrp-box">
                    <Label>MRP</Label>
                    <Input readOnly value={(p?.mrp || 0).toFixed(3)} />
                  </div>

                  <div className="box gradedisc-box">
                    <Label>Grade Disc (%)</Label>
                    <Input readOnly value={p?.gradeDiscountPct || 0} />
                  </div>

                  <div className="box saleprice-box">
                    <Label>Sale Price (incl GST)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      name="salePrice"
                      value={p?.salePrice || 0}
                      onChange={(e) => handleChangePrice(e, index)}
                    />
                  </div>

                  <div className="box basicprice-box">
                    <Label>Basic Price (ex GST)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      name="basicPrice"
                      value={p?.basicPrice || 0}
                      onChange={(e) => handleChangePrice(e, index)}
                    />
                  </div>

                  <div className="box gst-box">
                    <Label>GST %</Label>
                    <Input disabled value={p?.gstPercentage || 0} />
                  </div>

                  <div className="box basictotal-box">
                    <Label>Basic Total</Label>
                    <Input
                      disabled
                      value={
                        p?.taxableAmount
                          ? Number(p?.taxableAmount).toFixed(2)
                          : 0
                      }
                    />
                  </div>

                  <div className="box remove-box">
                    {index ? (
                      <Button
                        type="button"
                        color="danger"
                        className="btn-xxs"
                        onClick={() => removeMoreProduct(index)}
                        style={{ marginTop: 24 }}
                        aria-label="Remove row"
                      >
                        X
                      </Button>
                    ) : null}
                  </div>
                </div>
              ))}

            <div className="d-flex justify-content-end mt-2">
              <Button color="primary" size="sm" onClick={addMoreProduct}>
                Add Product
              </Button>
            </div>

            {/* ================= BILL ADJUSTMENTS + LEFT TABLES / RIGHT TOTALS ================= */}
            <div className="justify-content-total jt-container mt-3">
              {/* LEFT: Adjustments + HSN Summary */}
              <div className="left-tables jt-left">
                <div className="mini-title jt-title">Bill Adjustments</div>

                {/* toolbar */}
                <div className="adjust-toolbar jt-toolbar">
                  <div className="box adjust-addselect jt-addselect">
                    <Label className="jt-addselect-label">
                      Add Discount / Charge
                    </Label>
                    <Input
                      type="select"
                      className="jt-addselect-input"
                      onChange={onAddAdjustmentSelect}
                    >
                      <option value="">-- Select from master --</option>
                      {chargesMaster.map((x) => (
                        <option key={x._id} value={x._id}>
                          {x.title} ({x.type})
                        </option>
                      ))}
                    </Input>
                  </div>

                  <Button
                    size="sm"
                    color="success"
                    className="btn-xxs jt-btn jt-btn-discount"
                    onClick={addDiscountRow}
                  >
                    + Discount
                  </Button>
                  <Button
                    size="sm"
                    color="primary"
                    className="btn-xxs jt-btn jt-btn-charge"
                    onClick={addChargeRow}
                  >
                    + Charge
                  </Button>
                </div>

                {/* Adjustments table */}
                <div className="adjust-table jt-table">
                  <div className="adj-row adj-head jt-row jt-head">
                    <div className="jt-h-idx">#</div>
                    <div className="jt-h-type">Type</div>
                    <div className="jt-h-title">Title / Master</div>
                    <div className="jt-h-method">Method</div>
                    <div className="jt-h-pct">%</div>
                    <div className="jt-h-amt">Amount</div>
                    <div className="jt-h-applied">Applied</div>
                    <div className="jt-h-action">Action</div>
                  </div>

                  {/* DISCOUNTS */}
                  {selectedDiscountRows.map((r, i) => {
                    const isPct =
                      (r.discounttype || "").toLowerCase() === "percentage";
                    return (
                      <div
                        className="adj-row jt-row jt-discount-row"
                        key={`disc-${i}`}
                      >
                        <div className="c-idx jt-idx">{i + 1}</div>
                        <div className="c-type jt-type">Discount</div>

                        <div className="c-title jt-title-cell">
                          <Input
                            type="select"
                            className="jt-input jt-select jt-title-select"
                            value={r.id || ""}
                            onChange={(e) =>
                              pickMasterRow("Discount", i, e.target.value)
                            }
                          >
                            <option value="">-- Select from master --</option>
                            {chargesMaster
                              .filter((m) =>
                                (m.type || "")
                                  .toLowerCase()
                                  .includes("discount")
                              )
                              .map((m) => (
                                <option key={m._id} value={m._id}>
                                  {m.title}
                                </option>
                              ))}
                          </Input>
                        </div>

                        <div className="c-method jt-method-cell">
                          <Input
                            type="select"
                            className="jt-input jt-select jt-method-select"
                            value={r.discounttype || ""}
                            onChange={(e) =>
                              changeRowField(
                                "Discount",
                                i,
                                "discounttype",
                                e.target.value
                              )
                            }
                          >
                            <option value="">Choose</option>
                            <option value="Percentage">Percentage</option>
                            <option value="Amount">Amount</option>
                          </Input>
                        </div>

                        <div className="c-pct jt-pct-cell">
                          <Input
                            type="number"
                            inputMode="decimal"
                            placeholder="%"
                            className="jt-input jt-number jt-pct-input"
                            disabled={!isPct}
                            value={r.percentage === "" ? "" : r.percentage}
                            onChange={(e) =>
                              changeRowField(
                                "Discount",
                                i,
                                "percentage",
                                e.target.value
                              )
                            }
                          />
                        </div>

                        <div className="c-amt jt-amt-cell">
                          <Input
                            type="number"
                            inputMode="decimal"
                            placeholder="₹"
                            className="jt-input jt-number jt-amt-input"
                            disabled={isPct}
                            value={r.amount === "" ? "" : r.amount}
                            onChange={(e) =>
                              changeRowField(
                                "Discount",
                                i,
                                "amount",
                                e.target.value
                              )
                            }
                          />
                        </div>

                        <div className="c-applied jt-applied-cell">
                          <Input
                            readOnly
                            className="jt-input jt-readonly jt-applied-input"
                            value={r.__applied ?? 0}
                          />
                        </div>

                        <div className="c-action jt-action-cell">
                          <Button
                            color="danger"
                            size="sm"
                            className="btn-xxs jt-btn jt-delete-btn"
                            onClick={() => removeDiscountRow(i)}
                            aria-label="Delete"
                          >
                            🗑
                          </Button>
                        </div>
                      </div>
                    );
                  })}

                  {/* CHARGES */}
                  {selectedChargesRows.map((r, i) => {
                    const isPct =
                      (r.discounttype || "").toLowerCase() === "percentage";
                    return (
                      <div
                        className="adj-row jt-row jt-charge-row"
                        key={`charge-${i}`}
                      >
                        <div className="c-idx jt-idx">{i + 1}</div>
                        <div className="c-type jt-type">Charge</div>

                        <div className="c-title jt-title-cell">
                          <Input
                            type="select"
                            className="jt-input jt-select jt-title-select"
                            value={r.id || ""}
                            onChange={(e) =>
                              pickMasterRow("Charges", i, e.target.value)
                            }
                          >
                            <option value="">-- Select from master --</option>
                            {chargesMaster
                              .filter((m) =>
                                (m.type || "").toLowerCase().includes("charge")
                              )
                              .map((m) => (
                                <option key={m._id} value={m._id}>
                                  {m.title}
                                </option>
                              ))}
                          </Input>
                        </div>

                        <div className="c-method jt-method-cell">
                          <Input
                            type="select"
                            className="jt-input jt-select jt-method-select"
                            value={r.discounttype || ""}
                            onChange={(e) =>
                              changeRowField(
                                "Charges",
                                i,
                                "discounttype",
                                e.target.value
                              )
                            }
                          >
                            <option value="">Choose</option>
                            <option value="Percentage">Percentage</option>
                            <option value="Amount">Amount</option>
                          </Input>
                        </div>

                        <div className="c-pct jt-pct-cell">
                          <Input
                            type="number"
                            inputMode="decimal"
                            placeholder="%"
                            className="jt-input jt-number jt-pct-input"
                            disabled={!isPct}
                            value={r.percentage === "" ? "" : r.percentage}
                            onChange={(e) =>
                              changeRowField(
                                "Charges",
                                i,
                                "percentage",
                                e.target.value
                              )
                            }
                          />
                        </div>

                        <div className="c-amt jt-amt-cell">
                          <Input
                            type="number"
                            inputMode="decimal"
                            placeholder="₹"
                            className="jt-input jt-number jt-amt-input"
                            disabled={isPct}
                            value={r.amount === "" ? "" : r.amount}
                            onChange={(e) =>
                              changeRowField(
                                "Charges",
                                i,
                                "amount",
                                e.target.value
                              )
                            }
                          />
                        </div>

                        <div className="c-applied jt-applied-cell">
                          <Input
                            readOnly
                            className="jt-input jt-readonly jt-applied-input"
                            value={r.__applied ?? 0}
                          />
                        </div>

                        <div className="c-action jt-action-cell">
                          <Button
                            color="danger"
                            size="sm"
                            className="btn-xxs jt-btn jt-delete-btn"
                            onClick={() => removeChargeRow(i)}
                            aria-label="Delete"
                          >
                            🗑
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {hsnSummary.rows.length > 0 && (
                <div className="mt-2 jt-hsn-block">
                  <div className="mini-title jt-hsn-title">HSN Summary</div>
                  <Table bordered size="sm" className="jt-hsn-table">
                    <thead className="jt-hsn-thead">
                      <tr className="jt-hsn-headrow">
                        <th className="jt-hsn-th jt-hsn-col-hsn">HSN/SAC</th>
                        <th className="jt-hsn-th jt-hsn-col-taxable text-end">
                          TAXABLE VALUE
                        </th>
                        <th className="jt-hsn-th jt-hsn-col-rate text-center">
                          TAX RATE
                        </th>
                        {!hsnSummary.isIGST && (
                          <th className="jt-hsn-th jt-hsn-col-cgst text-end">
                            CGST
                          </th>
                        )}
                        {!hsnSummary.isIGST && (
                          <th className="jt-hsn-th jt-hsn-col-sgst text-end">
                            SGST
                          </th>
                        )}
                        {hsnSummary.isIGST && (
                          <th className="jt-hsn-th jt-hsn-col-igst text-end">
                            IGST
                          </th>
                        )}
                        <th className="jt-hsn-th jt-hsn-col-total text-end">
                          TOTAL
                        </th>
                      </tr>
                    </thead>
                    <tbody className="jt-hsn-tbody">
                      {hsnSummary.rows.map((r, i) => (
                        <tr
                          className="jt-hsn-row"
                          key={`hsn-${r.kind}-${r.hsn}-${i}`}
                        >
                          <td className="jt-hsn-td jt-hsn-col-hsn">{r.hsn}</td>
                          <td className="jt-hsn-td jt-hsn-col-taxable text-end">
                            {r.taxable.toFixed(2)}
                          </td>
                          <td className="jt-hsn-td jt-hsn-col-rate text-center">
                            {`${r.ratePct}%`}
                          </td>
                          {!hsnSummary.isIGST && (
                            <td className="jt-hsn-td jt-hsn-col-cgst text-end">
                              {(r.cgst || 0).toFixed(2)}
                            </td>
                          )}
                          {!hsnSummary.isIGST && (
                            <td className="jt-hsn-td jt-hsn-col-sgst text-end">
                              {(r.sgst || 0).toFixed(2)}
                            </td>
                          )}
                          {hsnSummary.isIGST && (
                            <td className="jt-hsn-td jt-hsn-col-igst text-end">
                              {(r.igst || 0).toFixed(2)}
                            </td>
                          )}
                          <td className="jt-hsn-td jt-hsn-col-total text-end">
                            {r.total.toFixed(2)}
                          </td>
                        </tr>
                      ))}
                      <tr className="jt-hsn-row jt-hsn-totalrow">
                        <td className="jt-hsn-td jt-hsn-col-hsn jt-bold">
                          TOTAL
                        </td>
                        <td className="jt-hsn-td jt-bold text-end">
                          {round2(hsnSummary.totals.taxable).toFixed(2)}
                        </td>
                        <td />
                        {!hsnSummary.isIGST && (
                          <td className="jt-hsn-td jt-bold text-end">
                            {round2(hsnSummary.totals.cgst).toFixed(2)}
                          </td>
                        )}
                        {!hsnSummary.isIGST && (
                          <td className="jt-hsn-td jt-bold text-end">
                            {round2(hsnSummary.totals.sgst).toFixed(2)}
                          </td>
                        )}
                        {hsnSummary.isIGST && (
                          <td className="jt-hsn-td jt-bold text-end">
                            {round2(hsnSummary.totals.igst).toFixed(2)}
                          </td>
                        )}
                        <td className="jt-hsn-td jt-bold text-end">
                          {round2(hsnSummary.totals.total).toFixed(2)}
                        </td>
                      </tr>
                    </tbody>
                  </Table>
                </div>
              )}

              {/* RIGHT: Totals + Submit */}
              <div className="right-summary jt-right">
                <ul className="totals-ul jt-totals">
                  <li className="jt-total-row">
                    <span className="jt-total-label">Basic Total</span>
                    <strong className="jt-total-val">
                      {Number(sumLineBasics(product)).toFixed(2)}
                    </strong>
                  </li>
                  <li className="jt-total-row">
                    <span className="jt-total-label">Discount Total</span>
                    <strong className="jt-total-val">
                      {selectedDiscountRows
                        .reduce((a, r) => a + (Number(r.__applied) || 0), 0)
                        .toFixed(2)}
                    </strong>
                  </li>
                  <li className="jt-total-row">
                    <span className="jt-total-label">Charges Total</span>
                    <strong className="jt-total-val">
                      {selectedChargesRows
                        .reduce((a, r) => a + (Number(r.__applied) || 0), 0)
                        .toFixed(2)}
                    </strong>
                  </li>
                  <li className="jt-total-row">
                    <span className="jt-total-label">Taxable Value</span>
                    <strong className="jt-total-val">
                      {(finalPrice > 0
                        ? Number(finalPrice)
                        : Number(sumLineBasics(product))
                      ).toFixed(2)}
                    </strong>
                  </li>

                  {GSTData?.Tax?.IgstTaxType ? (
                    <li className="jt-total-row">
                      <span className="jt-total-label">
                        IGST (incl. on charges)
                      </span>
                      <strong className="jt-total-val">
                        {Number(
                          (
                            Number(GSTData?.Tax?.IgstTotal || 0) +
                            Number(taxRate || 0)
                          ).toFixed(2)
                        ).toFixed(2)}
                      </strong>
                    </li>
                  ) : (
                    <>
                      <li className="jt-total-row">
                        <span className="jt-total-label">
                          SGST (incl. on charges)
                        </span>
                        <strong className="jt-total-val">
                          {Number(
                            (
                              Number(GSTData?.Tax?.SgstTotal || 0) +
                              Number(taxRate || 0) / 2
                            ).toFixed(2)
                          ).toFixed(2)}
                        </strong>
                      </li>
                      <li className="jt-total-row">
                        <span className="jt-total-label">
                          CGST (incl. on charges)
                        </span>
                        <strong className="jt-total-val">
                          {Number(
                            (
                              Number(GSTData?.Tax?.CgstTotal || 0) +
                              Number(taxRate || 0) / 2
                            ).toFixed(2)
                          ).toFixed(2)}
                        </strong>
                      </li>
                    </>
                  )}

                  <li className="jt-total-row">
                    <span className="jt-total-label">RoundOff</span>
                    <strong className="jt-total-val">
                      {Number(localRoundOff || 0).toFixed(2)}
                    </strong>
                  </li>

                  <hr className="jt-hr" />
                  <li className="jt-total-row jt-grand">
                    <span className="jt-total-label">Grand Total</span>
                    <strong className="jt-total-val">
                      {Number(finalPriceAfterTax || 0).toFixed(2)}
                    </strong>
                  </li>
                </ul>

                <div className="d-flex justify-content-end jt-submit">
                  <Button
                    color="primary"
                    type="submit"
                    className="mt-2 jt-submit-btn"
                    disabled={Loading}
                  >
                    {Loading ? "Submitting..." : "Submit"}
                  </Button>
                </div>
              </div>
            </div>
          </Form>
        </CardBody>
      </Card>

      {/* Simple remark modal kept for parity (not used in POST) */}
      <Modal isOpen={remarkModel} toggle={() => setRemarkModel(!remarkModel)}>
        <ModalHeader toggle={() => setRemarkModel(!remarkModel)}>
          Note (optional)
        </ModalHeader>
        <ModalBody>
          <Label htmlFor="remark">Message</Label>
          <Input
            type="textarea"
            id="remark"
            placeholder="Add a note..."
            value={remarkMessage}
            onChange={(e) => setRemarkMessage(e.target.value)}
          />
        </ModalBody>
      </Modal>
    </div>
  );
};

export default CreateChallan;
